import {
  Box,
  Button,
  ButtonDropdown,
  Container,
  Header,
  Input,
  SegmentedControl,
  SpaceBetween,
  StatusIndicator,
  Textarea,
} from '@cloudscape-design/components';
import PLCTextEditor from 'features/plc/components/PLCTextEditor';
import {
  SETUP_TYPE,
  SUB_MODE,
  TS_PATHS,
} from 'features/plc/contracts/testStand.schema';
import {
  usePLCValue,
  usePLCValues,
  usePLCWriteState,
} from 'features/plc/providers/PLCProvider';
import {
  buildPLCSnapshot,
  getSnapshotHandles,
} from 'features/testStand/utils/plcSnapshot';
import React, { useEffect, useState } from 'react';

import { useTestStand } from '../providers/TestStandProvider';

export default function SetupSelector() {
  const {
    state,
    setTestNotes,
    saveTestLog,
    resetTestMetadata,
  } = useTestStand();
  const [localTestNotes, setLocalTestNotes] = useState(state.testNotes);

  useEffect(() => {
    setLocalTestNotes(state.testNotes);
  }, [state.testNotes]);
  const { runWrite, activeWrite } = usePLCWriteState();
  const setupType = usePLCValue(TS_PATHS.run.setupType) || SETUP_TYPE.CONTINUOUS_FLOW;
  const subMode = usePLCValue(TS_PATHS.run.subMode) || SUB_MODE.DISPLACEMENT;
  const plcValues = usePLCValues(getSnapshotHandles(setupType, subMode));
  const plcSnapshot = buildPLCSnapshot(plcValues);

  const { status: saveStatus, error: saveError } = state.saveState;
  const saving = saveStatus === 'saving';

  const handleSetupChange = async (id) => {
    if (id === 'continuous') {
      await runWrite({ path: TS_PATHS.run.setupType, value: SETUP_TYPE.CONTINUOUS_FLOW });
    } else if (id === 'discrete_displacement') {
      await Promise.all([
        runWrite({ path: TS_PATHS.run.setupType, value: SETUP_TYPE.DISCRETE_FLOW }),
        runWrite({ path: TS_PATHS.run.subMode, value: SUB_MODE.DISPLACEMENT }),
      ]);
    } else if (id === 'discrete_pressure') {
      await Promise.all([
        runWrite({ path: TS_PATHS.run.setupType, value: SETUP_TYPE.DISCRETE_FLOW }),
        runWrite({ path: TS_PATHS.run.subMode, value: SUB_MODE.PRESSURE }),
      ]);
    }
  };

  let selectedSetupId = 'continuous';
  if (setupType === SETUP_TYPE.DISCRETE_FLOW) {
    selectedSetupId = subMode === SUB_MODE.PRESSURE ? 'discrete_pressure' : 'discrete_displacement';
  }

  const handleSaveTestLog = async () => {
    const testLogId = await saveTestLog(undefined, plcSnapshot);
    await runWrite({ path: TS_PATHS.run.testLogId, value: testLogId });
  };

  const handleStartNewTestLog = async () => {
    resetTestMetadata();
    await runWrite({ path: TS_PATHS.run.testName, value: '' });
    await runWrite({ path: TS_PATHS.run.testLogId, value: '' });
  };

  return (
    <Container header={<Header variant="h2">Setup</Header>}>
      <SpaceBetween size="m">
        <Box>
          <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>Setup type</Box>
          <SegmentedControl
            selectedId={selectedSetupId}
            options={[
              { id: 'continuous', text: 'Setup 1 — Continuous Flow' },
              { id: 'discrete_displacement', text: 'Setup 2.1 — Discrete Flow - Displacement' },
              { id: 'discrete_pressure', text: 'Setup 2.2 — Discrete Flow - Pressure' },
            ]}
            onChange={({ detail }) => handleSetupChange(detail.selectedId)}
          />
        </Box>
        <Container
          header={(
            <Header
              variant="h3"
              actions={(
                <SpaceBetween size="xs" direction="horizontal">
                  <Button
                    loading={saving}
                    disabled={Boolean(activeWrite)}
                    onClick={() => handleSaveTestLog()}
                  >
                    {state.testLogId ? 'Save metadata' : 'Create test log'}
                  </Button>
                  <ButtonDropdown
                    items={[{ id: 'new', text: 'Start a new test log' }]}
                    loading={saving}
                    disabled={saving}
                    onItemClick={handleStartNewTestLog}
                  >
                    Test log actions
                  </ButtonDropdown>
                </SpaceBetween>
              )}
            >
              Test details
            </Header>
          )}
        >
          <SpaceBetween size="s">
            <PLCTextEditor
              field={{
                label: 'Test name',
                path: TS_PATHS.run.testName,
              }}
            />
            <Box>
              <Box variant="awsui-key-label" margin={{ bottom: 'xxs' }}>Test notes</Box>
              <Textarea
                value={localTestNotes}
                placeholder="Optional notes for the whole test"
                onChange={({ detail }) => setLocalTestNotes(detail.value)}
                onBlur={() => setTestNotes(localTestNotes)}
              />
            </Box>
            <Box>
              <Box variant="awsui-key-label" margin={{ bottom: 'xxs' }}>Test log ID</Box>
              <Input value={state.testLogId || 'Not created'} readOnly />
            </Box>
            {state.isMetadataDirty && saveStatus !== 'saving' && (
              <StatusIndicator type="warning">Unsaved changes</StatusIndicator>
            )}
            {saveStatus === 'saving' && (
              <StatusIndicator type="loading">Saving…</StatusIndicator>
            )}
            {saveStatus === 'saved' && !state.isMetadataDirty && (
              <StatusIndicator type="success">Saved</StatusIndicator>
            )}
            {saveStatus === 'error' && (
              <StatusIndicator type="error">{saveError || 'Failed to save'}</StatusIndicator>
            )}
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </Container>
  );
}
